/**
 * WeDriveFinder: WE드라이브에서 파일찾기 다이얼로그
 * 원본: wehago-conference PLWeDriveFinder + DocumentShareButtonContainer 구조 기반
 *
 * 파일을 선택하면 getDocumentDetail API로 이미지 리소스 변환 후 onConfirm 호출.
 *
 * HTML 구조 및 CSS 클래스: 원본 PLWeDriveFinder의 dialog2.css 기반 마크업 사용
 */
import React, { useEffect, useRef, useState } from 'react';
import apiManager from '@/api';
import { formatFileSize, formatDateString } from '@/utils/formatUtils';
import { serialize } from '@/utils/urlUtils';
import { getCookie } from '@/utils/cookie';
import './wedriveFinder.css';

// ─── 타입 ──────────────────────────────────────────────────────────────────

interface WedriveInitInfo {
  weDriveTokenId: string;
  userId: string;
  directoryPath: string;
  fileUniqueKey: string;
  viewType: string | number;
  indexUseMode: string | boolean;
  sortName: string;
  sortType: number;
  bizShareFolder: string;
}

interface WedriveFile {
  fileName: string;
  fileName2?: string;
  fileType?: string;
  extentionType: string;
  mimeType: string;
  path: string;
  pathView?: string;
  fileUniqueKey: string;
  parentFileUniqueKey: string;
  _size: number;
  size: string;
  uploadDate: string;
  lastModifyDate: string;
  lock: boolean;
  directory: boolean;
  thumbNailPath?: string;
  shareFolder?: boolean;
  favorite?: boolean;
  subDirectory?: boolean;
  folderCount?: number;
  fileCount?: number;
  fileExtension?: string;
  fileSize?: string;
  isLock?: boolean;
  imagePath?: string;
}

interface PathEntry {
  fileName: string;
  directoryPath: string;
  fileUniqueKey: string;
  parentFileUniqueKey: string;
}

interface WeDriveFinderProps {
  onClose: () => void;
  onConfirm: (resources: string[], fileName: string, fileUniqueKey: string) => void;
}

// ─── 아이콘 URL 유틸 (PLWeDriveFinderUtils.getImageURL 포팅) ──────────────

const getFileIconURL = (fileType: string, ext: string, options?: { isUpSide?: boolean; isLock?: boolean; isShared?: boolean; isCompany?: boolean }): string => {
  let iconName = 'ico';
  let type = fileType?.toLowerCase() || '';
  const extension = ext?.toLowerCase() || '';

  // API가 type을 빈 문자열이나 'etc'로 줬을 때 확장자로 유추
  if (!type || type === 'etc') {
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'psd'].includes(extension)) type = 'image';
    else if (['mp4', 'avi', 'wmv', 'flv', 'swf', 'asf', 'mpg'].includes(extension)) type = 'video';
    else if (['zip', 'zipx', '7z', 'alz', 'rar'].includes(extension)) type = 'compressed';
    else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'hwp', 'show', 'cell', 'oneffice', 'pages', 'numbers'].includes(extension)) type = 'office';
    else if (['mp3', 'wav', 'wma'].includes(extension)) type = 'music';
    else if (['html', 'htm'].includes(extension)) type = 'web';
    else if (['ttf', 'otf'].includes(extension)) type = 'font';
    else type = 'etc';
  }

  if (type === 'folder') {
    if (options?.isCompany) iconName += '_company';
    else iconName += '_folder';

    if (options?.isUpSide) {
      iconName += '_up';
    } else {
      if (options?.isLock) iconName += '_secure';
      if (options?.isShared) iconName += '_share_send';
    }
    iconName += '_35.png';
    return 'https://static.wehago.com/imgs/common/icon_file/32x32/' + iconName;
  }

  switch (type) {
    case 'image':
      if (['bmp', 'gif', 'png', 'psd'].includes(extension)) iconName += '_' + extension;
      else if (['jpg', 'jpeg'].includes(extension)) iconName += '_jpg';
      else iconName += '_image_etc';
      break;
    case 'video':
      if (['asf', 'avi', 'flv', 'mp4', 'mpg', 'swf', 'wmv'].includes(extension)) iconName += '_' + extension;
      else iconName += '_video_etc';
      break;
    case 'compressed':
      if (['zip', 'zipx', '7z', 'alz', 'rar'].includes(extension)) iconName += '_' + extension;
      else iconName += '_zip_etc';
      break;
    case 'office':
      if (['cell', 'doc', 'docx', 'hwp', 'numbers', 'oneffice', 'pages', 'pdf', 'ppt', 'pptx', 'show', 'xls', 'xlsx'].includes(extension)) iconName += '_' + extension;
      else iconName += '_file_etc';
      break;
    case 'music':
      if (['mp3', 'wav', 'wma'].includes(extension)) iconName += '_' + extension;
      else iconName += '_music_etc';
      break;
    case 'web':
      if (['html', 'htm'].includes(extension)) iconName += '_' + extension;
      else iconName += '_html_etc';
      break;
    case 'font':
      if (['otf', 'ttf'].includes(extension)) iconName += '_' + extension;
      else iconName += '_font_etc';
      break;
    case 'etc':
      if (extension === 'txt') iconName += '_' + extension;
      else iconName += '_file_etc';
      break;
    default:
      if (['ai', 'key'].includes(extension)) iconName += '_' + extension;
      else iconName += '_file_etc';
  }
  
  iconName += '_32.png';
  return 'https://static.wehago.com/imgs/common/icon_file/32x32/' + iconName;
};

// ─── 정렬 관련 상수 ──────────────────────────────────────────────────────

const SORT_COLUMNS = [
  { name: '종류', type: 'mimeType', width: 36 },
  { name: '이름', type: 'fileName', width: undefined }, // auto
  { name: '크기', type: 'size', width: 130 },
  { name: '업로드한 날짜', type: 'uploadDate', width: 130 },
  { name: '수정한 날짜', type: 'lastModifyDate', width: 145 },
];

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────

const WeDriveFinder: React.FC<WeDriveFinderProps> = ({ onClose, onConfirm }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const [initInfo, setInitInfo] = useState<WedriveInitInfo | null>(null);
  const [fileList, setFileList] = useState<WedriveFile[]>([]);
  const [currentPath, setCurrentPath] = useState('');
  const [currentFileUniqueKey, setCurrentFileUniqueKey] = useState('');
  const [currentParentFileUniqueKey, setCurrentParentFileUniqueKey] = useState('');
  const [pathHistory, setPathHistory] = useState<PathEntry[]>([]);
  const [selectedFile, setSelectedFile] = useState<WedriveFile | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(1);
  const [sortName, setSortName] = useState('fileName');
  const [sortType, setSortType] = useState(0); // 0 = up, 1 = down
  const [viewMode, setViewMode] = useState<'detail' | 'image'>('detail');
  const [searchWord, setSearchWord] = useState('');

  const initInfoRef = useRef<WedriveInitInfo | null>(null);

  // ── 지원 확장자 / 파일 크기 제한 ─────────────────────────────────────────

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const SUPPORTED_EXTENSIONS = /^(hwp|doc|docx|rtf|xls|xlsx|cell|ppt|pptx|show|bmp|gif|jpg|jpeg|txt|one|png|pdf)$/i;

  // ── 파일 목록 로드 ──────────────────────────────────────────────────────

  const loadFileList = async (params: {
    initInfo: WedriveInitInfo;
    directoryPath: string;
    fileUniqueKey: string;
    parentFileUniqueKey: string;
    page?: number;
    sortName?: string;
    sortType?: number;
  }) => {
    setIsLoading(true);
    setSelectedFile(null);
    setError(null);
    const {
      initInfo: info,
      directoryPath,
      fileUniqueKey,
      parentFileUniqueKey,
      page = 1,
      sortName: sName = 'fileName',
      sortType: sType = 0,
    } = params;

    const data: Record<string, unknown> = {
      indexUseMode: info.indexUseMode,
      tokenId: info.weDriveTokenId,
      userId: info.userId,
      directoryPath,
      fileUniqueKey,
      parentFileUniqueKey: parentFileUniqueKey && fileUniqueKey ? parentFileUniqueKey : '',
      viewType: info.viewType,
      sortName: sName,
      sortType: sType,
      currentPage: page,
      bizShareFolder: info.bizShareFolder,
      cno: apiManager.selectedCno,
      ccode: apiManager.selectedCCode,
    };

    try {
      const result = await apiManager.getWedriveFileList(data);
      setIsLoading(false);

      if (result.resultCode !== 200) {
        setError('파일 목록을 불러올 수 없습니다.');
        return;
      }

      const listData = result.resultData as { storageList?: WedriveFile[]; totalPageCnt?: number } | null;
      setFileList(listData?.storageList ?? []);
      setTotalPageCount(Math.ceil(listData?.totalPageCnt ?? 1));
    } catch {
      setIsLoading(false);
      setError('파일 목록을 불러올 수 없습니다.');
    }
  };

  // ── 초기화 ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      const result = await apiManager.getWedriveInitInfo();
      if (result.resultCode !== 200 || !result.resultData) {
        setIsLoading(false);
        setError('WE드라이브 정보를 불러올 수 없습니다.\n로그인 상태를 확인해주세요.');
        return;
      }
      const info = result.resultData as unknown as WedriveInitInfo;
      setInitInfo(info);
      initInfoRef.current = info;
      setCurrentPath(info.directoryPath);
      setCurrentFileUniqueKey(info.fileUniqueKey);
      setCurrentParentFileUniqueKey('');
      setSortName(info.sortName);
      setSortType(info.sortType);
      setCurrentPage(1);

      await loadFileList({
        initInfo: info,
        directoryPath: info.directoryPath,
        fileUniqueKey: info.fileUniqueKey,
        parentFileUniqueKey: '',
        page: 1,
        sortName: info.sortName,
        sortType: info.sortType,
      });
    };
    init();
  }, []);

  // ── 스크롤 초기화 ──────────────────────────────────────────────────────

  const handleScrollTop = () => {
    const scroll = document.getElementById('wedrive_file_list');
    if (scroll) scroll.scrollTop = 0;
  };

  // ── 폴더 클릭 ──────────────────────────────────────────────────────────

  const handleClickFolder = (file: WedriveFile) => {
    if (!initInfoRef.current) return;
    const newHistory = [
      ...pathHistory,
      {
        fileName: file.fileName,
        directoryPath: file.path,
        fileUniqueKey: file.fileUniqueKey,
        parentFileUniqueKey: file.parentFileUniqueKey,
      },
    ];
    setPathHistory(newHistory);
    setCurrentPath(file.path);
    setCurrentFileUniqueKey(file.fileUniqueKey);
    setCurrentParentFileUniqueKey(file.parentFileUniqueKey);
    setCurrentPage(1);
    handleScrollTop();
    loadFileList({
      initInfo: initInfoRef.current,
      directoryPath: file.path,
      fileUniqueKey: file.fileUniqueKey,
      parentFileUniqueKey: file.parentFileUniqueKey,
      page: 1,
      sortName,
      sortType,
    });
  };

  // ── 경로 이동 (breadcrumb 클릭) ─────────────────────────────────────────

  const handleClickPath = (index: number) => {
    if (!initInfoRef.current) return;
    if (index < 0) {
      setPathHistory([]);
      setCurrentPath(initInfoRef.current.directoryPath);
      setCurrentFileUniqueKey(initInfoRef.current.fileUniqueKey);
      setCurrentParentFileUniqueKey('');
      setCurrentPage(1);
      handleScrollTop();
      loadFileList({
        initInfo: initInfoRef.current,
        directoryPath: initInfoRef.current.directoryPath,
        fileUniqueKey: initInfoRef.current.fileUniqueKey,
        parentFileUniqueKey: '',
        page: 1,
        sortName,
        sortType,
      });
      return;
    }
    const entry = pathHistory[index];
    const trimmedHistory = pathHistory.slice(0, index + 1);
    setPathHistory(trimmedHistory);
    setCurrentPath(entry.directoryPath);
    setCurrentFileUniqueKey(entry.fileUniqueKey);
    setCurrentParentFileUniqueKey(entry.parentFileUniqueKey);
    setCurrentPage(1);
    handleScrollTop();
    loadFileList({
      initInfo: initInfoRef.current,
      directoryPath: entry.directoryPath,
      fileUniqueKey: entry.fileUniqueKey,
      parentFileUniqueKey: entry.parentFileUniqueKey,
      page: 1,
      sortName,
      sortType,
    });
  };

  // ── 파일 선택 ──────────────────────────────────────────────────────────

  const handleClickFile = (file: WedriveFile) => {
    if (isFolder(file)) {
      handleClickFolder(file);
      return;
    }
    setSelectedFile((prev) => (prev?.fileUniqueKey === file.fileUniqueKey ? null : file));
  };

  // ── 페이지 변경 ────────────────────────────────────────────────────────

  const handleChangePage = (page: number) => {
    if (!initInfoRef.current) return;
    setCurrentPage(page);
    handleScrollTop();
    loadFileList({
      initInfo: initInfoRef.current,
      directoryPath: currentPath,
      fileUniqueKey: currentFileUniqueKey,
      parentFileUniqueKey: currentParentFileUniqueKey,
      page,
      sortName,
      sortType,
    });
  };

  // ── 정렬 변경 ──────────────────────────────────────────────────────────

  const handleSort = (column: string) => {
    if (!initInfoRef.current) return;
    let newSortType = 0;
    if (sortName === column) {
      newSortType = sortType === 0 ? 1 : 0;
    }
    setSortName(column);
    setSortType(newSortType);
    setCurrentPage(1);
    handleScrollTop();
    loadFileList({
      initInfo: initInfoRef.current,
      directoryPath: currentPath,
      fileUniqueKey: currentFileUniqueKey,
      parentFileUniqueKey: currentParentFileUniqueKey,
      page: 1,
      sortName: column,
      sortType: newSortType,
    });
  };

  // ── 검색 ───────────────────────────────────────────────────────────────

  const handleSearch = () => {
    if (!initInfoRef.current || !searchWord.trim()) return;
    const info = initInfoRef.current;
    setIsLoading(true);
    setSelectedFile(null);
    apiManager.getWedriveSearchKeyword(searchWord.trim(), {
      tokenId: info.weDriveTokenId,
      userId: info.userId,
      indexUseMode: info.indexUseMode,
      viewType: info.viewType,
      bizShareFolder: info.bizShareFolder,
      cno: apiManager.selectedCno,
      ccode: apiManager.selectedCCode,
    }).then((result) => {
      setIsLoading(false);
      if (result.resultCode === 200) {
        const listData = result.resultData as { storageList?: WedriveFile[]; totalPageCnt?: number } | null;
        setFileList(listData?.storageList ?? []);
        setTotalPageCount(Math.ceil(listData?.totalPageCnt ?? 1));
      }
    }).catch(() => {
      setIsLoading(false);
    });
  };

  // ── 확인 (파일 변환) ───────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!selectedFile) return;
    console.log('[WeDrive] selectedFile:', selectedFile);

    if (selectedFile._size > MAX_FILE_SIZE) {
      setAlertMessage('공유 파일 용량 초과\n공유 가능한 파일 용량을 초과하였습니다.\n20MB 이하의 파일을 선택해주세요.');
      return;
    }

    if (!SUPPORTED_EXTENSIONS.test(selectedFile.extentionType)) {
      setAlertMessage(
        `지원하지 않는 파일 형식입니다. (${selectedFile.extentionType})\n` +
          '지원 형식: hwp, doc(x), xls(x), ppt(x), bmp, gif, jpg, png, pdf'
      );
      return;
    }

    const method = apiManager.getDocumentMethod(selectedFile.extentionType, selectedFile.mimeType);
    if (!method) {
      setError(`지원하지 않는 파일 형식입니다. (${selectedFile.extentionType})`);
      return;
    }

    setIsConverting(true);

    const cno = apiManager.selectedCno;
    const data: Record<string, unknown> = {
      cno,
      FileName: selectedFile.fileUniqueKey,
      Ext: selectedFile.extentionType,
      ServiceCode: 'wedrive',
      ServiceKey: '',
      BucketType: 'C',
      isWedrive: true,
      isFullPreview: true,
      target_cno: cno,
    };

    if (method === 'getAttachmentsPublicURL') {
      data['TokenID'] = getCookie('AUTH_A_TOKEN');
    }

    try {
      const documentData = await apiManager.getDocumentDetail(serialize(data), method);
      setIsConverting(false);

      if (documentData.status !== 200) {
        setError('문서 변환에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      if (documentData.data.resultCode === 'E2021') {
        setError('파일 변환 중입니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      if (documentData.data.resultCode !== '0000') {
        setError('문서 변환에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      let resources: string[] = [];
      if (method === 'getImageURL') {
        resources = documentData.data.resultList as string[];
      } else if (method === 'getOfficePreView') {
        const first = documentData.data.resultList?.[0] as { resources?: string[] } | undefined;
        resources = first?.resources ?? [];
      }

      onConfirm(resources, selectedFile.fileName, selectedFile.fileUniqueKey);
    } catch (err) {
      setIsConverting(false);
      setError('문서 변환 중 오류가 발생했습니다.');
    }
  };

  // ── 렌더 ───────────────────────────────────────────────────────────────

  const isFolder = (f: WedriveFile) => f.directory;
  const isSelected = (f: WedriveFile) => selectedFile?.fileUniqueKey === f.fileUniqueKey;
  const hasSelection = !!selectedFile && !isConverting;

  // breadcrumb items
  const breadcrumbItems = [
    { label: 'WE드라이브', index: -1 },
    ...pathHistory.map((p, i) => ({ label: p.fileName, index: i })),
  ];

  // sort direction class
  const getSortClass = (colType: string) => {
    const isActive = sortName === colType;
    const direction = sortType === 0 ? 'up' : 'down';
    return `LUX_basic_btn Image btn_data${isActive ? ' on' : ''}${isActive ? ' ' + direction : ' down'}`;
  };

  // 페이지네이션
  const renderPagination = () => {
    if (totalPageCount <= 1) return null;
    const pages = Array.from({ length: totalPageCount }, (_, i) => i + 1);
    // pagingBlock: 5페이지씩
    const blockSize = 5;
    const currentBlock = Math.ceil(currentPage / blockSize);
    const startPage = (currentBlock - 1) * blockSize + 1;
    const endPage = Math.min(startPage + blockSize - 1, totalPageCount);
    const visiblePages = pages.filter((p) => p >= startPage && p <= endPage);

    return (
      <div className="paging_area">
        <div className="LUX_basic_page" style={{ marginTop: 20 }}>
          <div className="pagein">
            {startPage > 1 && (
              <a className="prev" onClick={() => handleChangePage(startPage - 1)} style={{ cursor: 'pointer' }}>
                {'<'}
              </a>
            )}
            {visiblePages.map((page) => (
              <a
                key={page}
                className={`num${page === currentPage ? ' on' : ''}`}
                onClick={() => handleChangePage(page)}
                style={{ cursor: 'pointer' }}
              >
                {page}
              </a>
            ))}
            {endPage < totalPageCount && (
              <a className="next" onClick={() => handleChangePage(endPage + 1)} style={{ cursor: 'pointer' }}>
                {'>'}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 오버레이 — LUXDialog dimmed 대체 */}
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }}
        onClick={onClose}
      />

      {/* 다이얼로그 — dialog_wrap > dialog_content sm 구조 (원본 PLWeDriveFinder와 동일) */}
      <div
        className="dialog_wrap"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 1201,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid #e7e7e7',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog_content sm">
          <div className="dialog_data">
            {/* 헤더 — dialog_data_tit clearbx */}
            <div className="dialog_data_tit clearbx">
              <h1>WE드라이브에서 파일찾기</h1>
              <button type="button" className="LUX_basic_btn btn_clr" onClick={onClose}>
                <span className="sp_lux">닫기</span>
              </button>
            </div>

            {/* 컨텐츠 — dialog_data_area nopadding */}
            <div className="dialog_data_area nopadding">
              <div className="nav_file_box">
                {/* ─ 경로 네비게이션 (SearchBox 영역) ─ */}
                <div className="nav_file_path">
                  <div className="path_item ellipsis" style={{ maxWidth: 453 }}>
                    {breadcrumbItems.map((bc, idx) => {
                      if (idx === breadcrumbItems.length - 1) {
                        // 마지막 항목: 현재 위치 (파란색)
                        return (
                          <span key={idx} className="item_text text_blue">
                            {bc.label}
                          </span>
                        );
                      }
                      return (
                        <span
                          key={idx}
                          className="item_text"
                          onClick={() => handleClickPath(bc.index)}
                          style={{ cursor: 'pointer' }}
                        >
                          {bc.label}
                        </span>
                      );
                    })}
                  </div>
                  {/* 뷰모드 전환 버튼 */}
                  <div className="btnbx">
                    <button
                      type="button"
                      className="LUX_basic_btn Image basic"
                      style={{ padding: 7, backgroundColor: '#f3f3f3' }}
                      onClick={() => setViewMode('detail')}
                    >
                      <span className={`sp_lux sp_listtype${viewMode === 'detail' ? ' on' : ''}`} />
                    </button>
                    <button
                      type="button"
                      className="LUX_basic_btn Image basic"
                      style={{ marginLeft: -1, padding: 6, backgroundColor: '#f3f3f3' }}
                      onClick={() => setViewMode('image')}
                    >
                      <span className={`sp_lux sp_thumbtype${viewMode === 'image' ? ' on' : ''}`} />
                    </button>
                  </div>
                  {/* 검색 */}
                  <div className="search_item">
                    <div className="wdf-search-box">
                      <input
                        type="text"
                        placeholder="검색어를 입력해주세요."
                        value={searchWord}
                        onChange={(e) => setSearchWord(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <button className="wdf-search-btn" type="button" onClick={handleSearch}>
                        🔍
                      </button>
                    </div>
                  </div>
                </div>

                {/* ─ 파일 테이블 (detail 모드) ─ */}
                {viewMode === 'detail' ? (
                  <div className="LUX_basic_tbl">
                    {/* 테이블 헤더 — FileViewHeader */}
                    <table className="tblarea size_md">
                      <colgroup>
                        <col style={{ width: 36 }} />
                        <col style={{ width: 35 }} />
                        <col />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 145 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="cellft" scope="col">
                            <div className="inbx" />
                          </th>
                          {SORT_COLUMNS.map((col) => (
                            <th key={col.type} className="celcnt" scope="col">
                              <div className="inbx">
                                <button
                                  type="button"
                                  className={getSortClass(col.type)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSort(col.type);
                                  }}
                                >
                                  {col.name}
                                  <span className="sp_lux" />
                                </button>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                    </table>

                    {/* 파일 리스트 — FileViewList (tbl_scrall) */}
                    {isLoading || isConverting ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220, flexDirection: 'column', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#666' }}>{isConverting ? '문서를 변환 중입니다...' : '불러오는 중...'}</div>
                      </div>
                    ) : error ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
                        <div style={{ fontSize: 13, color: '#e74c3c', whiteSpace: 'pre-line', textAlign: 'center' }}>{error}</div>
                      </div>
                    ) : (
                      <div className="tbl_scrall" id="wedrive_file_list" style={{ height: 220 }}>
                        <div className="tbl_scrall_in">
                          <table className="tblarea size_md">
                            <colgroup>
                              <col style={{ width: 36 }} />
                              <col style={{ width: 35 }} />
                              <col />
                              <col style={{ width: 130 }} />
                              <col style={{ width: 130 }} />
                              <col style={{ width: 145 }} />
                            </colgroup>
                            <tbody>
                              {fileList.length === 0 ? (
                                <tr>
                                  <td colSpan={6}>
                                    <div className="empty">
                                      <div className="wedrv_nodata_area svc_empty txtcnt">
                                        <span className="nodata_text">표시할 내용이 없습니다.</span>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                fileList.map((file) => {
                                  const folder = isFolder(file);
                                  const selected = isSelected(file);
                                  const fileType = file.fileType?.toLowerCase() || (folder ? 'folder' : 'etc');
                                  const ext = file.extentionType?.toLowerCase() || file.fileExtension?.toLowerCase() || '';
                                  const iconURL = getFileIconURL(fileType, ext, {
                                    isLock: file.lock || file.isLock,
                                    isShared: file.shareFolder,
                                  });

                                  return (
                                    <tr
                                      key={file.fileUniqueKey}
                                      className={selected ? 'select' : ''}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClickFile(file);
                                      }}
                                    >
                                      {/* 라디오/체크 */}
                                      <td className="cellft" style={{ lineHeight: 'initial' }}>
                                        <div className="inbx">
                                          {!folder && (
                                            <span className="LUX_basic_switch" style={{ verticalAlign: 'middle' }}>
                                              <span className="LUXrabx">
                                                <input
                                                  type="radio"
                                                  checked={selected}
                                                  readOnly
                                                />
                                                <span className="sp_lux" />
                                              </span>
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      {/* 아이콘 */}
                                      <td>
                                        <div className="inbx">
                                          <div className="thum_img">
                                            <img src={iconURL} alt="file_icon" />
                                          </div>
                                        </div>
                                      </td>
                                      {/* 파일명 */}
                                      <td className="cellft" style={{ lineHeight: 'initial' }}>
                                        <div className="inbx ellipsis">
                                          {file.fileName}
                                        </div>
                                      </td>
                                      {/* 크기 */}
                                      <td className="celcnt" style={{ lineHeight: 'initial' }}>
                                        <div className="inbx ellipsis">
                                          {folder ? '' : formatFileSize(file._size)}
                                        </div>
                                      </td>
                                      {/* 업로드 날짜 */}
                                      <td className="celcnt" style={{ lineHeight: 'initial' }}>
                                        <div className="inbx">{formatDateString(file.uploadDate)}</div>
                                      </td>
                                      {/* 수정 날짜 */}
                                      <td className="celcnt" style={{ lineHeight: 'initial' }}>
                                        <div className="inbx">{formatDateString(file.lastModifyDate)}</div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ─ 썸네일 뷰 (image 모드) ─ */
                  <>
                    {isLoading || isConverting ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, flexDirection: 'column', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#666' }}>{isConverting ? '문서를 변환 중입니다...' : '불러오는 중...'}</div>
                      </div>
                    ) : error ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
                        <div style={{ fontSize: 13, color: '#e74c3c', whiteSpace: 'pre-line', textAlign: 'center' }}>{error}</div>
                      </div>
                    ) : (
                      <div className="thumbnail_viewlist" id="wedrive_file_list">
                        <ul className="clearfix">
                          {fileList.map((file) => {
                            const folder = isFolder(file);
                            const selected = isSelected(file);
                            const fileType = file.fileType?.toLowerCase() || (folder ? 'folder' : 'etc');
                            const ext = file.extentionType?.toLowerCase() || '';
                            const iconURL = getFileIconURL(fileType, ext, {
                              isLock: file.lock || file.isLock,
                              isShared: file.shareFolder,
                            });
                            const imgSrc = fileType === 'image' && file.imagePath ? file.imagePath : iconURL;

                            return (
                              <li
                                key={file.fileUniqueKey}
                                className={selected ? 'on' : ''}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickFile(file);
                                }}
                              >
                                {!folder && (
                                  <span className="LUX_basic_switch">
                                    <span className="LUXrabx">
                                      <input type="radio" checked={selected} readOnly />
                                      <span className="sp_lux" />
                                    </span>
                                  </span>
                                )}
                                <span className="img_bx">
                                  <img src={imgSrc} alt={file.fileName} />
                                </span>
                                <div className="txt_bx ellipsis" style={{ lineHeight: 'initial' }}>
                                  {file.fileName}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        {fileList.length === 0 && (
                          <div className="empty">
                            <div className="wedrv_nodata_area svc_empty txtcnt">
                              <span className="nodata_text">표시할 내용이 없습니다.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* 페이지네이션 */}
              {renderPagination()}
            </div>
          </div>
        </div>

        {/* 버튼 영역 — dialog_btnbx (원본 LUXButton 구조) */}
        <div className="dialog_btnbx" style={{ margin: '20px -30px -20px', padding: '14px 0', background: '#f2f2f2', borderTop: '1px solid #e7e7e7' }}>
          <div>
            <button
              type="button"
              className="LUX_basic_btn basic Confirm"
              onClick={onClose}
            >
              <span>취소</span>
            </button>
            <button
              type="button"
              className={`LUX_basic_btn Confirm ${hasSelection ? 'basic2' : 'disable'}`}
              style={{ marginLeft: 4 }}
              onClick={hasSelection ? handleConfirm : undefined}
              disabled={!hasSelection}
            >
              <span>확인</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Alert 모달 다이얼로그 (20MB 초과 등) ── */}
      {alertMessage && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* 반투명 배경 */}
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.2)' }} onClick={() => setAlertMessage(null)} />
          
          <div className="dialog_wrap size_alert" style={{ position: 'relative', width: 360, background: '#fff', border: '1px solid #888', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
            <div className="dialog_content">
              <div className="dialog_data">
                <div className="dialog_data_area" style={{ textAlign: 'center', padding: '40px 20px 20px' }}>
                  <div style={{ marginBottom: 15 }}>
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block' }}>
                      <path d="M30 15L48 45H12L30 15Z" stroke="#ffb300" strokeWidth="3" strokeLinejoin="round"/>
                      <path d="M30 25V35M30 40H30.01" stroke="#ffb300" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <strong style={{ display: 'block', fontSize: 18, color: '#000', marginBottom: 15 }}>{alertMessage?.split('\n')[0]}</strong>
                  <p style={{ fontSize: 13, color: '#000', lineHeight: 1.6 }}>
                    {alertMessage?.split('\n').slice(1).map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>)}
                  </p>
                </div>
                <div className="dialog_btnbx" style={{ padding: '15px 0', borderTop: '1px solid #e5e5e5', backgroundColor: '#f9f9f9', textAlign: 'center', margin: 0 }}>
                  <button type="button" className="LUX_basic_btn basic2 Confirm" onClick={() => setAlertMessage(null)} style={{ minWidth: 80 }}>
                    <span>확인</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeDriveFinder;
